import { Component, type ReactNode } from "react";

// Catches render-time exceptions anywhere below this boundary and shows a
// recoverable fallback instead of letting React unmount the whole tree.
//
// Pair with the `unhandledrejection` listener in main.tsx for async errors —
// that catches stray rejected promises which don't trigger this boundary.
//
// We deliberately keep this dependency-free (no logging-service import) so a
// failing logger never escalates a recoverable render error into a hard crash.

interface State {
  err: Error | null;
}

interface Props {
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info.componentStack);
  }

  private reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.err) return this.props.children;
    // Short, generic fallback. The `err.message` is shown so staff have a
    // grep-able string to give Support, but no stack frames / source paths.
    const code = (this.state.err.message ?? "unknown").slice(0, 200);
    return (
      <div className="min-h-screen grid place-items-center bg-bg text-fg p-6">
        <div className="max-w-md text-center space-y-4">
          <div className="font-display text-3xl">Something went wrong</div>
          <p className="text-fg-muted text-sm">
            The page hit an error and couldn't recover. Reloading usually fixes it.
            If this keeps happening, send Support this code: <code className="text-accent">{code}</code>.
          </p>
          <button
            type="button"
            onClick={this.reload}
            className="focus-ring inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent text-accent-fg font-medium hover:opacity-90 transition-opacity"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
